import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  Tooltip,
  FormHelperText,
} from '@mui/material';
import { modules, actions, moduleActions } from './permissionConfig';

const initializePrivileges = () => {
  const initialPrivs: any = {};
  modules.forEach((mod: string) => {
    initialPrivs[mod] = {};
    actions.forEach((act: string) => {
      initialPrivs[mod][act] = false;
    });
  });
  return initialPrivs;
};

// Helper to format camelCase to spaced words
const formatModuleName = (name: string) =>
  name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());

// Helper to normalize privileges object (only allowed actions)
const normalizePrivileges = (privs: any) => {
  const normalized: any = {};
  modules.forEach((mod) => {
    normalized[mod] = {};
    (moduleActions[mod] || []).forEach((act) => {
      normalized[mod][act] = privs?.[mod]?.[act] ?? false;
    });
  });
  return normalized;
};

// Helper to check if view permission is required for an action
const requiresViewPermission = (action: string) => {
  return ['Add', 'Edit', 'Delete', 'Block', 'Unblock', 'Approve', 'Credit', 'Debit', 'Settle', 'Export'].includes(action);
};

// Helper to validate permissions - check if view is selected when other actions are selected
const validatePermissions = (privs: any) => {
  const errors: string[] = [];
  
  modules.forEach((mod) => {
    const modulePermissions = privs[mod] || {};
    const hasView = modulePermissions['View'] || false;
    // If any dependent action is selected without view, show one error per module
    const dependentActionSelected = Object.keys(modulePermissions).some(
      (action) => requiresViewPermission(action) && modulePermissions[action]
    );
    if (dependentActionSelected && !hasView) {
      errors.push(`${formatModuleName(mod)}: Edit/Delete/Block/Unblock requires View permission`);
    }
  });
  
  return errors;
};

// Helper to check if any permission is selected
const hasAnyPermission = (privileges: any) => {
  return modules.some((mod) =>
    Object.values(privileges[mod] || {}).some((value) => value === true)
  );
};

const PrivilegeMatrix = ({
  value,
  onChange,
  field = { value: {}, onChange: () => { } },
  error,
  isTouched,
}: any) => {
  // Memoize initial values to prevent unnecessary re-renders
  const initialPrivilegesData = useMemo(() => 
    normalizePrivileges(field.value?.permissions || value?.permissions || initializePrivileges()),
    [field.value?.permissions, value?.permissions]
  );
  
  const initialPrivilegeStatus = useMemo(() => 
    field.value?.privilege ?? value?.privilege ?? false,
    [field.value?.privilege, value?.privilege]
  );

  const [privileges, setPrivileges] = useState(initialPrivilegesData);
  // const [isAllSelected, setIsAllSelected] = useState(false);
  const [allSelect, setAllSelect] = useState(initialPrivilegeStatus);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Memoize the onChange callback to prevent infinite loops
  const handleChange = useCallback((newValue: any) => {
    if (field.onChange) {
      field.onChange({
        target: {
          name: field.name || 'permission',
          value: newValue,
        },
      });
    }

    if (onChange) {
      onChange(newValue);
    }
  }, [field.onChange, field.name, onChange]);

  // Only update privileges when the external value actually changes
  useEffect(() => {
    const externalPermissions = field.value?.permissions || value?.permissions;
    const externalPrivilege = field.value?.privilege ?? value?.privilege;
    
    if (externalPermissions) {
      const normalized = normalizePrivileges(externalPermissions);
      setPrivileges(normalized);
    }
    
    if (externalPrivilege !== undefined) {
      setAllSelect(externalPrivilege);
    }
  }, [field.value?.permissions, value?.permissions, field.value?.privilege, value?.privilege]);

  // useEffect(() => {
  //   const allChecked = modules.every((mod) =>
  //     actions.every((act) => privileges?.[mod]?.[act])
  //   );
  //   setIsAllSelected(allChecked);
  // }, [privileges]);

  // Validate permissions whenever privileges change
  useEffect(() => {
    const errors = validatePermissions(privileges);
    setValidationErrors(errors);
  }, [privileges]);

  // Call handleChange when privileges or allSelect changes
  useEffect(() => {
    const currentValue = { privilege: allSelect, permissions: privileges };
    const externalValue = field.value || value;
    
    // Only call handleChange if the value has actually changed
    if (JSON.stringify(currentValue) !== JSON.stringify(externalValue)) {
      handleChange(currentValue);
    }
  }, [privileges, allSelect, handleChange, field.value, value]);

  const isActionAvailable = (module: string, action: string) => {
    return privileges[module] && action in privileges[module];
  };

  const handleCheck = (module: string, action: string) => {
    // Don't allow changes if the permission doesn't exist
    if (!isActionAvailable(module, action)) {
      return;
    }

    // if (action === 'Block' || action === 'Unblock') {
    //   const isCurrentlyChecked = privileges[module][action];
    //   // If unchecking either, uncheck both
    //   if (isCurrentlyChecked) {
    //     const updated = normalizePrivileges({
    //       ...privileges,
    //       [module]: {
    //         ...privileges[module],
    //         Block: false,
    //         Unblock: false,
    //       },
    //     });
    //     setPrivileges(updated);
    //     return;
    //   } else {
    //     // If checking either, check both (and ensure View is checked)
    //     const updated = normalizePrivileges({
    //       ...privileges,
    //       [module]: {
    //         ...privileges[module],
    //         Block: true,
    //         Unblock: true,
    //         View: true,
    //       },
    //     });
    //     setPrivileges(updated);
    //     return;
    //   }
    // }

    // If trying to uncheck View, uncheck all permissions in that row
    if (action === 'View' && privileges[module][action]) {
      const updated = normalizePrivileges({
        ...privileges,
        [module]: {
          ...privileges[module],
          // Uncheck all permissions in this module
          ...Object.keys(privileges[module]).reduce((acc, act) => {
            acc[act] = false;
            return acc;
          }, {} as any),
        },
      });
      setPrivileges(updated);
      return;
    }

    // If trying to check an action that requires view, ensure view is also checked
    if (requiresViewPermission(action) && !privileges[module]['View']) {
      // Automatically check View when checking an action that requires it
      const updated = normalizePrivileges({
        ...privileges,
        [module]: {
          ...privileges[module],
          [action]: !privileges[module][action],
          'View': true, // Automatically check View
        },
      });
      setPrivileges(updated);
      return;
    }

    const updated = normalizePrivileges({
      ...privileges,
      [module]: {
        ...privileges[module],
        [action]: !privileges[module][action],
      },
    });
    setPrivileges(updated);
  };

  const handleSelectAll = () => {
    const newAllSelect = !allSelect;
    setAllSelect(newAllSelect);

    const updated = normalizePrivileges({ ...privileges });
    modules.forEach((mod) => {
      updated[mod] = { ...updated[mod] };
      actions.forEach((act) => {
        // Only update if the action is available for this module
        if (isActionAvailable(mod, act)) {
          updated[mod][act] = newAllSelect;
        }
      });
    });

    setPrivileges(updated);
  };
 

  return (
    <Box sx={{ width: '100%' }}>
      {/* Show error message above the component */}
      {isTouched && error && (
        <FormHelperText 
          error 
          sx={{ 
            mb: 1.5, 
            fontSize: 12, 
            lineHeight: 1.2,
            ml: 0 
          }}
        >
          {error}
        </FormHelperText>
      )}
      
      {/* Show validation errors */}
      {validationErrors.length > 0 && (
        <FormHelperText 
          error 
          sx={{ 
            mb: 1.5, 
            fontSize: 12, 
            lineHeight: 1.2,
            ml: 0 
          }}
        >
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </FormHelperText>
      )}
      
      {/* Show error if no permissions selected - only if no error prop */}
      {isTouched && !hasAnyPermission(privileges) && !error && (
        <FormHelperText 
          error 
          sx={{ 
            mb: 1.5, 
            fontSize: 12, 
            lineHeight: 1.2,
            ml: 0 
          }}
        >
          Please select at least one permission
        </FormHelperText>
      )}
      
      {/* Privilege Select All Section */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          mb: 2,
          pb: 1.5,
        }}
      >
        <Typography
          sx={{
            color: 'rgba(0, 0, 0, 0.87)',
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'Livvic',
            mr: 2,
          }}
        >
          Privilege:
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={allSelect}
              onChange={handleSelectAll}
              sx={{
                color: '#666',
                '&.Mui-checked': { color: '#1976d2' },
                '&.MuiCheckbox-indeterminate': { color: '#1976d2' },
              }}
            />
          }
          label={
            <Typography 
              sx={{ 
                fontFamily: 'Livvic',
                fontSize: 14,
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.87)'
              }}
            >
              Select All
            </Typography>
          }
          sx={{ marginLeft: 0 }}
        />
      </Box>
      
      {/* Compact Classic Table Layout */}
      <Box 
        sx={{ 
          overflowX: 'auto',
          border: '1px solid #ddd',
          backgroundColor: '#fff',
          maxHeight: '600px',
          overflowY: 'auto',
        }}
      >
        <table
          style={{
            borderCollapse: 'collapse',
            width: '100%',
            fontFamily: 'Livvic',
            fontSize: 13,
          }}
        >
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
              <th
                style={{
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#333',
                  borderRight: '1px solid #ddd',
                  minWidth: '150px',
                  width: '150px',
                  fontSize: 13,
                }}
              >
                Module
              </th>
              {actions.map((act) => (
                <th
                  key={act}
                  style={{
                    padding: '8px 4px',
                    textAlign: 'center',
                    fontWeight: 600,
                    color: '#333',
                    borderRight: '1px solid #ddd',
                    minWidth: '65px',
                    fontSize: 12,
                  }}
                >
                  {act}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modules.map((mod, modIndex) => {
              const moduleActionsList = moduleActions[mod] || [];
              if (moduleActionsList.length === 0) return null;
              
              return (
                <tr 
                  key={mod}
                  style={{
                    borderBottom: '1px solid #ddd',
                    backgroundColor: modIndex % 2 === 0 ? '#fff' : '#fafafa',
                  }}
                >
                  <td
                    style={{
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: '#333',
                      borderRight: '1px solid #ddd',
                      whiteSpace: 'nowrap',
                      fontSize: 13,
                    }}
                  >
                    {formatModuleName(mod)}
                  </td>
                  {actions.map((act) => {
                    const isActionAvailable = moduleActionsList.includes(act);
                    return (
                      <td 
                        key={act} 
                        style={{ 
                          textAlign: 'center',
                          padding: '4px 2px',
                          borderRight: '1px solid #ddd',
                        }}
                      >
                        {isActionAvailable ? (
                          <Tooltip
                            title={requiresViewPermission(act) ? `${act} requires View permission` : ''}
                            arrow
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '2px',
                              }}
                            >
                              <Checkbox
                                checked={privileges?.[mod]?.[act] || false}
                                onChange={() => handleCheck(mod, act)}
                                size="small"
                                sx={{
                                  color: '#666',
                                  padding: '2px',
                                  '&.Mui-checked': { color: '#1976d2' },
                                  '&.MuiCheckbox-indeterminate': { color: '#1976d2' },
                                  '& .MuiSvgIcon-root': {
                                    fontSize: 18,
                                  },
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '10px',
                                  fontFamily: 'Livvic',
                                  color: '#666',
                                  textAlign: 'center',
                                  fontWeight: 400,
                                  lineHeight: 1.1,
                                }}
                              >
                                {act}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default PrivilegeMatrix;
